/**
 * OperationsPanel — the Operations tab: the live readings, the risk-by-
 * workload heatmap and the live tail on the left; the priority alerts rail
 * on the right. Carbon tiles in a condensed grid with 1px gutters; the rail
 * stacks under the main column below 1056px. It follows the theme like the
 * rest of the content area (only the shell is pinned to Gray 100).
 *
 * Signal wiring:
 *   - heatmap, tail, lastUpdated — useOperationsData (shared caches)
 *   - priority alerts rail       — useOperationsAlerts (criticals-only,
 *                                   per the curation doctrine in
 *                                   .agents/Tasks/High Priority/
 *                                   operations-priority-alerts-curation.md §3)
 *
 * Both hooks share the active-drift query key with the Baseline tile, so
 * opening Operations after Posture fires no extra fetch for the drift list.
 *
 * Neutral bad states: the heatmap is a neutral ramp with Coral only for its
 * critical bucket (D13, the --heatmap-l* tokens), severity reads by shape
 * then word, and only critical takes a hue.
 */

import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import type { DashboardStats, M365Workload, UserInsightsSummary } from '@/types'
import type { HeatLevel } from '@/lib/workloadMapping'
import { useOperationsData, type HeatmapCell } from '@/hooks/useOperationsData'
import { useOperationsAlerts } from '@/hooks/useOperationsAlerts'
import type { OperationsAlert } from '@/lib/operations-alerts'
import { IconInfo, SeverityGlyph, Skeleton, StatusDot, Tag, Tooltip } from '@/components/carbon'

interface Props {
  tenantId: string | undefined
  guardianScore: number | null
  stats: DashboardStats | null | undefined
  insights: UserInsightsSummary | null | undefined
  bpaScore: number | null | undefined
}

export default function OperationsPanel({
  tenantId,
  guardianScore,
  stats,
  insights,
  bpaScore,
}: Props) {
  const navigate = useNavigate()
  const baseline = stats?.complianceScore && stats.complianceScore.totalChecks > 0 ? stats.complianceScore.percentage : null
  const mfa = insights?.hasData ? insights.mfaMemberAdoptionPercentage : null

  // Heatmap, tail, sync from the shared multi-pillar composer.
  const operations = useOperationsData(tenantId, stats)
  const { heatmap, tail, lastUpdated } = operations

  // Priority alerts rail — criticals-only feed per the curation doctrine.
  const {
    alerts,
    totalCandidates,
    hiddenCountsBySource,
    nonCriticalDriftCount,
    isLoading: alertsLoading,
    error: alertsError,
  } = useOperationsAlerts(tenantId)

  const overflow = totalCandidates > alerts.length
  // The open-alerts reading reflects the rail's curated candidate pool, not the
  // raw stats count, so the reading and the rail's count agree.
  const openAlerts = totalCandidates

  return (
    <div
      id="panel-operations"
      role="tabpanel"
      aria-labelledby="tab-operations"
      className="mt-8 grid grid-cols-1 gap-px border border-line-subtle bg-line-subtle c-lg:grid-cols-[minmax(0,1fr)_360px]"
    >
      {/* Main column */}
      <div className="min-w-0 bg-layer-1 p-4 c-md:p-6">
        <dl className="m-0 grid grid-cols-2 gap-px border border-line-subtle bg-line-subtle c-md:grid-cols-3 c-xlg:grid-cols-6">
          <Reading label="Baseline" value={pct(baseline)} />
          <Reading label="Best Practice" value={pct(bpaScore ?? null)} />
          <Reading label="MFA" value={pct(mfa)} />
          <Reading
            label="Open alerts"
            value={alertsLoading ? null : String(openAlerts)}
            dot={!alertsLoading && openAlerts > 0 ? <StatusDot tone="crit" label="Critical alerts open" /> : null}
          />
          <Reading label="Last sync" value={formatSync(lastUpdated)} />
          <Reading label="Guardian Score" value={guardianScore == null ? null : String(Math.round(guardianScore))} />
        </dl>

        {/* Heatmap */}
        <section aria-labelledby="ops-heatmap-heading" className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <h3 id="ops-heatmap-heading" className="m-0 text-heading font-normal text-text-primary">
              Risk by workload
            </h3>
            <ul className="m-0 flex list-none flex-wrap items-center gap-x-4 gap-y-1 p-0 text-helper text-text-secondary">
              {LEGEND.map(l => (
                <li key={l} className="inline-flex items-center gap-1.5">
                  <span aria-hidden="true" className="block h-2.5 w-2.5" style={heatStyle(l)} />
                  {HEAT_LABEL[l]}
                </li>
              ))}
            </ul>
          </div>

          {heatmap.workloads.length === 0 ? (
            <p className="m-0 mt-4 border-t border-line-subtle py-6 text-helper text-text-helper">
              Nothing to review. No workload shows a drift, failing-control or coverage signal.
            </p>
          ) : (
            <div className="mt-4 min-w-0 overflow-x-auto">
              <div
                className="grid min-w-[480px] gap-1"
                style={{ gridTemplateColumns: `110px repeat(${heatmap.workloads.length}, minmax(48px, 1fr))` }}
              >
                <span aria-hidden="true" />
                {heatmap.workloads.map(w => (
                  <span key={w} className="py-1.5 text-center text-helper text-text-helper">
                    {w}
                  </span>
                ))}
                {heatmap.rows.map(row => (
                  <HeatRow
                    key={row.label}
                    label={row.label}
                    cells={row.cells}
                    workloads={heatmap.workloads}
                    onCellSelect={hrefForHeatCell(row.label)}
                    navigate={navigate}
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Live tail */}
        <section aria-labelledby="ops-tail-heading" className="mt-8">
          <div className="flex items-center justify-between gap-4">
            <h3 id="ops-tail-heading" className="m-0 text-heading font-normal text-text-primary">
              Live tail
            </h3>
            <Tag>Live</Tag>
          </div>
          <div className="mt-4 max-h-72 overflow-auto bg-layer-2 px-4 py-3 font-mono text-helper text-text-secondary">
            {tail.length === 0 ? (
              <p className="m-0 font-sans text-text-helper">No activity in the last 24 hours.</p>
            ) : (
              tail.map((line, i) => (
                <div key={i} className="whitespace-pre-wrap [overflow-wrap:anywhere]">
                  <span className="mr-2.5 text-text-helper">{line.stamp}</span>
                  <span className={LEVEL_CLASS[line.level]}>[{line.level}]</span> {line.msg}
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Right rail — priority alerts */}
      <aside aria-labelledby="ops-alerts-heading" className="min-w-0 bg-layer-1 p-4 c-md:p-6">
        <div className="flex items-center gap-2">
          <h3 id="ops-alerts-heading" className="m-0 text-heading font-normal text-text-primary">
            Priority alerts
          </h3>
          <PriorityAlertsInfo />
          {alerts.length > 0 && (
            <Tag className="ml-auto tabular-nums">{overflow ? `${alerts.length} of ${totalCandidates}` : alerts.length}</Tag>
          )}
        </div>

        {alertsLoading ? (
          <Skeleton lines={4} label="Loading priority alerts" className="mt-4" />
        ) : alertsError ? (
          <p className="m-0 mt-4 border-t border-line-subtle py-4 text-body text-text-secondary" role="status">
            Priority alerts did not load. Refresh the page to try again.
          </p>
        ) : alerts.length === 0 ? (
          <p className="m-0 mt-4 border-t border-line-subtle py-4 text-body text-text-secondary">
            Nothing to review. Critical detections from the last 14 days appear here.
          </p>
        ) : (
          <ul className="m-0 mt-4 list-none p-0">
            {alerts.map(a => (
              <li key={a.id}>
                <Alert alert={a} onSelect={href => navigate(href)} />
              </li>
            ))}
          </ul>
        )}

        {/* Criticals overflow footer: per-source deep links when the rail truncates. */}
        {!alertsLoading && !alertsError && overflow && (
          <CriticalOverflowFooter
            hiddenCount={totalCandidates - alerts.length}
            counts={hiddenCountsBySource}
            onNavigate={navigate}
          />
        )}

        {/* Non-critical drift backlog, surfaced without admitting it to the rail (§10 Q1 in the plan). */}
        {!alertsLoading && !alertsError && (
          <NonCriticalDriftFooter
            high={nonCriticalDriftCount.high}
            medium={nonCriticalDriftCount.medium}
            onClick={() => navigate('/configuration-drift?status=Open')}
          />
        )}
      </aside>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function pct(v: number | null | undefined): string | null {
  return v == null || !Number.isFinite(v) ? null : `${Math.round(v)}%`
}

function Reading({ label, value, dot }: { label: string; value: string | null; dot?: React.ReactNode }) {
  return (
    <div className="min-w-0 bg-layer-1 px-4 py-3">
      <dt className="text-helper text-text-helper">{label}</dt>
      <dd className="m-0 mt-1 flex items-center gap-2 text-body font-semibold tabular-nums text-text-primary">
        {dot}
        {value ?? (
          <>
            <span aria-hidden="true">–</span>
            <span className="sr-only">Not available</span>
          </>
        )}
      </dd>
    </div>
  )
}

/** Heat levels onto the five --heatmap tokens: a neutral ramp with Coral only on the critical bucket (D13). */
const HEAT_TOKEN: Record<HeatLevel, 0 | 1 | 2 | 3 | 4> = {
  l1: 0,
  l2: 1,
  l3: 2,
  l4: 3,
  l5: 4,
}

function heatStyle(level: HeatLevel): CSSProperties {
  return { background: `var(--heatmap-l${HEAT_TOKEN[level]}-bg)` }
}

const HEAT_LABEL: Record<HeatLevel, string> = {
  l1: 'healthy',
  l2: 'low',
  l3: 'watch',
  l4: 'warn',
  l5: 'critical',
}

const LEGEND: HeatLevel[] = ['l1', 'l3', 'l4', 'l5']

function HeatRow({
  label,
  cells,
  workloads,
  onCellSelect,
  navigate,
}: {
  label: string
  cells: Array<HeatmapCell | null>
  workloads: M365Workload[]
  /** Returns the route for a click, or null when the row should stay static. */
  onCellSelect: ((wl: M365Workload) => string | null) | null
  navigate: (path: string) => void
}) {
  return (
    <>
      <span className="self-center pl-1 text-left text-helper text-text-secondary">{label}</span>
      {cells.map((cell, i) => {
        const wl = workloads[i]
        if (!cell) {
          // No signal for this workload-row combo: a gap that reads as "not measured", not "healthy".
          return (
            <span
              key={`${label}-${wl}`}
              className="block h-8 border border-dashed border-line-subtle"
              aria-label={`${label} · ${wl} · not measured`}
              title={`${label} · ${wl} · not measured`}
            />
          )
        }
        const tooltip = `${label} · ${wl} · ${HEAT_LABEL[cell.level]}`
        const href = onCellSelect ? onCellSelect(wl) : null
        if (href) {
          return (
            <button
              key={`${label}-${wl}`}
              type="button"
              onClick={() => navigate(href)}
              className="block h-8 cursor-pointer transition-[outline-color] duration-110 ease-carbon hover:outline hover:outline-1 hover:-outline-offset-1 hover:outline-[color:var(--text-primary)]"
              style={heatStyle(cell.level)}
              aria-label={tooltip}
              title={tooltip}
            />
          )
        }
        return (
          <span
            key={`${label}-${wl}`}
            className="block h-8"
            style={heatStyle(cell.level)}
            aria-label={tooltip}
            title={tooltip}
          />
        )
      })}
    </>
  )
}

/**
 * Map a heatmap row label to its drill-in URL builder. Returns null for rows
 * that don't have a clear destination (Coverage today — it's a data-pipeline
 * signal, not a finding, and there's no canonical "see coverage gaps" page).
 */
function hrefForHeatCell(
  rowLabel: string,
): ((wl: M365Workload) => string | null) | null {
  if (rowLabel === 'Best Practice') {
    return wl => `/security?tab=best-practices&workload=${encodeURIComponent(wl)}`
  }
  if (rowLabel === 'Drift') {
    // No workload filter on the drift queue today — the hash takes the user
    // to the full queue where they can filter by status / severity / date.
    return () => '/configuration-drift#full-queue'
  }
  return null
}

/** Only a critical line takes a hue (Coral ink); the other levels stay neutral. */
const LEVEL_CLASS = {
  ok: 'text-text-helper',
  info: 'text-text-secondary',
  warn: 'text-text-primary',
  crit: 'text-crit-ink',
} as const

const PILLAR_NAME: Record<OperationsAlert['pillar'], string> = {
  compliance: 'Baseline',
  bpa: 'Best Practice',
  identity: 'Identity',
}

const SEVERITY_WORD: Record<OperationsAlert['severity'], string> = {
  crit: 'Critical',
  high: 'High',
  med: 'Medium',
}

/**
 * Alert row.
 *
 * The left column renders either the event timestamp (drift and Best
 * Practice rows with a real `detectedAt`) or the state label `Open`
 * (Identity rows have no event time — they're current-state findings, and
 * faking a timestamp would mislead operators reading the rail as a feed).
 */
function Alert({
  alert,
  onSelect,
}: {
  alert: OperationsAlert
  onSelect: (href: string) => void
}) {
  const leftColumn = alert.detectedAt ? formatAlertStamp(alert.detectedAt) : 'Open'

  return (
    <button
      type="button"
      onClick={() => onSelect(alert.href)}
      className="grid w-full grid-cols-[3rem_minmax(0,1fr)] items-start gap-3 border-t border-line-subtle px-2 py-3 text-left transition-colors duration-110 ease-carbon hover:bg-layer-hover"
    >
      <span className="pt-0.5 text-helper tabular-nums text-text-helper">{leftColumn}</span>
      <span className="min-w-0 text-body text-text-secondary [overflow-wrap:anywhere]">
        <b className="block font-semibold text-text-primary">{alert.text}</b>
        {alert.detail && <span className="block">{alert.detail}</span>}
        <span className="mt-1 flex flex-wrap items-center gap-2 text-helper text-text-helper">
          <SeverityGlyph level={alert.severity} size={12}>
            {SEVERITY_WORD[alert.severity]}
          </SeverityGlyph>
          <span aria-hidden="true">·</span>
          {PILLAR_NAME[alert.pillar]}
        </span>
      </span>
    </button>
  )
}

/**
 * Info affordance next to the Priority alerts heading: a Carbon tooltip, so
 * operators don't read the deliberately curated count as a backlog total.
 */
function PriorityAlertsInfo() {
  return (
    <Tooltip label="A watch list of critical detections from the last 14 days, not a ranked fix list. For what to tackle this week by impact, see the Posture queue.">
      <span className="grid h-6 w-6 place-items-center text-[color:var(--icon-secondary)] hover:text-text-primary">
        <IconInfo />
        <span className="sr-only">About priority alerts</span>
      </span>
    </Tooltip>
  )
}

/**
 * Criticals overflow footer — renders when the rail truncates (e.g. the
 * curated admit list ran "8 of 42") and offers per-source deep links so
 * operators can land on the right queue, narrowed to critical and open.
 * Clauses for sources whose count is 0 are skipped; with no clause left it
 * renders nothing.
 */
function CriticalOverflowFooter({
  hiddenCount,
  counts,
  onNavigate,
}: {
  hiddenCount: number
  counts: { drift: number; shall: number; identity: number }
  onNavigate: (path: string) => void
}) {
  const clauses: Array<{ key: string; label: string; href: string }> = []
  if (counts.drift > 0) {
    clauses.push({
      key: 'drift',
      label: `${counts.drift} in Baseline`,
      href: '/configuration-drift?status=Open&severity=Critical',
    })
  }
  if (counts.shall > 0) {
    clauses.push({
      key: 'shall',
      label: `${counts.shall} in Best Practice`,
      href: '/security?tab=best-practices&criticality=SHALL&status=Open',
    })
  }
  if (counts.identity > 0) {
    clauses.push({
      key: 'identity',
      label: `${counts.identity} in Identity`,
      href: '/user-insights?tab=all&scope=globalAdmins',
    })
  }

  if (clauses.length === 0) return null

  return (
    <p className="m-0 mt-4 text-helper text-text-secondary">
      <span>{hiddenCount} more critical items are open. View </span>
      {clauses.map((c, i) => (
        <span key={c.key}>
          <button
            type="button"
            onClick={() => onNavigate(c.href)}
            className="text-left text-link hover:text-link-hover hover:underline"
          >
            {c.label}
          </button>
          {i < clauses.length - 1 ? <span>, </span> : <span>.</span>}
        </span>
      ))}
    </p>
  )
}

/**
 * Footer link below the alerts list — surfaces non-critical drifts also
 * open without admitting them to the criticals-only rail. Renders nothing
 * when there are no high or medium drifts open.
 */
function NonCriticalDriftFooter({
  high,
  medium,
  onClick,
}: {
  high: number
  medium: number
  onClick: () => void
}) {
  if (high <= 0 && medium <= 0) return null

  const parts: string[] = []
  if (high > 0) parts.push(`${high} High`)
  if (medium > 0) parts.push(`${medium} Medium`)
  const label = parts.join(' and ')
  const noun = high + medium === 1 ? 'drift is' : 'drifts are'

  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-4 block w-full text-left text-helper text-link hover:text-link-hover hover:underline"
    >
      {label} {noun} also open. View them in the queue.
    </button>
  )
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

const HOUR_MS = 60 * 60 * 1000
const DAY_MS  = 24 * HOUR_MS

function formatSync(iso: string | null): string | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  const ageMs = Math.max(0, Date.now() - t)
  if (ageMs < 60_000)       return 'just now'
  if (ageMs < HOUR_MS)      return `${Math.floor(ageMs / 60_000)}m ago`
  if (ageMs < DAY_MS)       return `${Math.floor(ageMs / HOUR_MS)}h ago`
  return `${Math.floor(ageMs / DAY_MS)}d ago`
}

// Alerts use a wall-clock stamp ("14:32") so the rail reads as a feed.
// Falls back to a dash if the source timestamp is unparseable.
function formatAlertStamp(iso: string | null | undefined): string {
  if (!iso) return '–'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '–'
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}
